import React, { useState, useEffect, useContext } from "react"
import { AlertsContext } from "../../../utils/providers/alerts"
import { db, auth } from "../../../utils/firebase"
import firebase from "firebase"

/**
 * UI components
 */
import WindowCard from "../../ui/window-card/window-card"
import Badge from "../../ui/badge/badge"
import { LoadingIcon } from "../../../utils/svgs"
import MiniVoucher from "./images/mini-voucher.png"

/**
 * Inline gift-voucher list + apply control for the Payments tab.
 *
 * Lists vouchers purchased for the client. A PAID voucher can be applied,
 * which writes a payment record into the booking and marks the voucher
 * as REDEEMED.
 */
export default function GiftVouchers({ bookingID, clientID, onApplied }) {
	const [fetching, setFetching] = useState(true)
	const [applying, setApplying] = useState(false)
	const [vouchers, setVouchers] = useState([])

	const { pushAlert } = useContext(AlertsContext)

	useEffect(() => {
		const fetchVouchers = async () => {
			const voucherDocs = await db.collection("gift_vouchers").where("purchased_for", "==", clientID).get()
			const allVouchers = voucherDocs.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
			setVouchers(allVouchers)
			setFetching(false)
		}
		fetchVouchers()
	}, [clientID])

	const applyVoucher = async (voucherID) => {
		if (applying) return

		const voucher = vouchers.find((v) => v.id === voucherID)

		if (!voucher) return

		if (voucher.status !== "PAID") {
			pushAlert({
				type: "ERROR",
				title: "Gift voucher is not available.",
			})
			return
		}

		setApplying(true)

		try {
			// Redeem atomically so the same voucher can't be applied twice
			await db.runTransaction(async (transaction) => {
				const voucherRef = db.doc(`gift_vouchers/${voucherID}`)
				const voucherDoc = await transaction.get(voucherRef)

				if (!voucherDoc.exists || voucherDoc.data().status !== "PAID") {
					throw new Error("Gift voucher is no longer available.")
				}

				const paymentRef = db.collection(`bookings/${bookingID}/payments`).doc()

				transaction.set(paymentRef, {
					amount: voucher.value ? Number.parseFloat(voucher.value) : 0,
					payee: "Gift Voucher",
					reference: `Gift Voucher - ${voucher.id}`,
					source: "VOUCHER",
					paid_date: firebase.firestore.Timestamp.now(),
					created: firebase.firestore.FieldValue.serverTimestamp(),
					saved_by: auth.currentUser.uid,
				})

				transaction.update(voucherRef, {
					status: "REDEEMED",
					bookingID: bookingID,
				})
			})

			setVouchers((prev) => prev.map((v) => (v.id === voucherID ? { ...v, status: "REDEEMED", bookingID } : v)))

			pushAlert({
				type: "SUCCESS",
				title: "Voucher applied",
			})

			if (typeof onApplied === "function") onApplied()
		} catch (err) {
			pushAlert({
				type: "ERROR",
				title: err?.message || "Could not apply the voucher.",
			})
		} finally {
			setApplying(false)
		}
	}

	return (
		<WindowCard
			title="Gift Vouchers"
			subtitle="Vouchers which have been pre-paid for and allocated onto the client's account will be applied as a payment.">
			<div className="gift-vouchers">
				{!fetching &&
					vouchers.length > 0 &&
					vouchers.map((voucher) => (
						<div
							key={voucher.id}
							className={["voucher", voucher.bookingID ? "not-available" : ""].join(" ")}
							onClick={() => applyVoucher(voucher.id)}>
							<img
								src={MiniVoucher}
								alt="Voucher"
							/>

							<div className="voucher-details">
								<p>£{voucher.value}</p>

								{voucher.status === "PENDING" && (
									<Badge
										type="NEGATIVE"
										label="Not paid"
									/>
								)}

								{voucher.status === "REDEEMED" && (
									<Badge
										type="POSITIVE"
										label="Redeemed"
									/>
								)}

								{voucher.status === "PAID" && (
									<Badge
										type="INFO"
										label="Available"
									/>
								)}
							</div>
						</div>
					))}
			</div>

			{!fetching && vouchers.length === 0 && <small className="no-vouchers">No gift vouchers available.</small>}

			{fetching && (
				<div className="vouchers-loading-wrapper">
					<p>Loading...</p>
					<div className="svg-loading-wrap">
						<LoadingIcon />
					</div>
				</div>
			)}
		</WindowCard>
	)
}
